import clsx from 'clsx';
import { AlertTriangle, WifiOff, PowerOff, PauseCircle, Navigation, Wrench } from 'lucide-react';
import type { Vehicle } from '../../types/vehicle';
import { VEHICLE_STATUS_LABELS, VEHICLE_STATUS_COLORS } from '../../utils/constants';
import { formatRelative, formatSpeed } from '../../utils/formatters';
import { MAINTENANCE_STATUS_LABELS, vehicleMaintenance } from '../../utils/maintenance';

interface VehicleStatusBadgeProps {
  status: Vehicle['status'];
  className?: string;
}

export function VehicleStatusBadge({ status, className }: VehicleStatusBadgeProps) {
  const icons: Record<Vehicle['status'], React.ReactNode> = {
    critical_event: <AlertTriangle size={12} />,
    no_signal: <WifiOff size={12} />,
    offline: <PowerOff size={12} />,
    stopped: <PauseCircle size={12} />,
    in_route: <Navigation size={12} />,
  };
  const color = VEHICLE_STATUS_COLORS[status];
  return (
    <span
      className={clsx('vehicle-status-badge', className)}
      style={{ color, background: `${color}18`, borderColor: `${color}44` }}
    >
      {icons[status]}
      {VEHICLE_STATUS_LABELS[status]}
    </span>
  );
}

interface VehicleListItemProps {
  vehicle: Vehicle;
  isSelected: boolean;
  onClick: () => void;
}

export function VehicleListItem({ vehicle, isSelected, onClick }: VehicleListItemProps) {
  // FE-23: el estado sale de `utils/maintenance.ts`, no de un `<= 30` propio.
  // Solo `expired` y `due_soon` avisan: "Sin datos" no es un problema del vehículo.
  const worst = vehicleMaintenance(vehicle).worst;
  const maintAlert = worst === 'expired' || worst === 'due_soon' ? worst : null;
  const maintColor = maintAlert === 'expired' ? 'var(--critical)' : 'var(--medium)';
  const maintBg = maintAlert === 'expired' ? 'var(--critical-bg)' : 'var(--medium-bg)';

  return (
    <button
      className={clsx('vehicle-list-item', isSelected && 'vehicle-list-item--selected')}
      onClick={onClick}
      id={`vehicle-item-${vehicle.id}`}
    >
      <div className="vehicle-list-item__dot"
        style={{ background: VEHICLE_STATUS_COLORS[vehicle.status] }}
      />
      <div className="vehicle-list-item__info">
        <div className="vehicle-list-item__top">
          <span className="vehicle-list-item__plate">{vehicle.plate}</span>
          {vehicle.activeAlerts > 0 && (
            <span className="vehicle-list-item__alerts">{vehicle.activeAlerts}</span>
          )}
          {maintAlert && (
            <span style={{ fontSize: '10px', background: maintBg, color: maintColor, border: `1px solid ${maintColor}`, borderRadius: '99px', padding: '1px 6px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
              <Wrench size={10} /> {MAINTENANCE_STATUS_LABELS[maintAlert]}
            </span>
          )}
        </div>
        <span className="vehicle-list-item__model">{vehicle.brand} {vehicle.model}</span>
        {vehicle.driverName && (
          <span className="vehicle-list-item__driver">{vehicle.driverName}</span>
        )}
        <div className="vehicle-list-item__meta">
          <VehicleStatusBadge status={vehicle.status} />
          {vehicle.status === 'in_route' && vehicle.position.speed !== undefined && (
            <span className="vehicle-list-item__speed">{formatSpeed(vehicle.position.speed)}</span>
          )}
        </div>
      </div>
      <div className="vehicle-list-item__time">{formatRelative(vehicle.lastSeen)}</div>
    </button>
  );
}
